import React from 'react'

// ACQ-3200: the analyst valuation cutover, on two independent levers.
//
//   FLEET   GATSBY_VALUATION_GQL_DEFAULT=v2 moves everyone to v2; back to v1
//           by setting it to anything else. Env change + redeploy, no code
//           change. Gatsby inlines GATSBY_* at build time, so this is a
//           deploy-speed lever, not a runtime one.
//   BROWSER `?gqlBackend=v1` / `?gqlBackend=v2` on any admin page pins THIS
//           browser, overriding the fleet default in either direction, and
//           persists in localStorage because Gatsby client-side navigation
//           drops query params. Instant, and the lever to reach for first
//           when a single analyst hits trouble.
//
// Only the allowlisted valuation operations below are routed to v2 (Apollo
// split link in apollo.tsx) — every other admin domain stays on v1, so the
// admin keeps working end to end without hitting not-yet-migrated v2 surface.
// The list grows per domain as the migration proceeds, and the whole file is
// removed once GATSBY_APOLLO_URL itself points at v2 (Phase 9).
//
// Both stacks write the same Postgres, so neither lever leaves divergent
// state to unwind.
//
// SECURITY: only allowlisted backend NAMES are accepted, resolved to
// build-time env URLs. Never accept a raw URL from the query string — the
// Apollo auth link attaches the Firebase bearer token to whatever host the
// client points at, so a crafted link carrying an attacker URL would
// exfiltrate admin tokens.

const STORAGE_KEY = 'flock.gqlBackend'

export type GqlBackendName = 'v1' | 'v2'

// client operation names (not schema root fields) fired by the admin
// valuation pages — inventory taken 2026-07-24 from
// components/HomeOps/ValuationsV1 + the copy-valuation page (ACQ-3120)
const VALUATION_CUTOVER_OPS: ReadonlySet<string> = new Set([
  // compute
  'AdminAddressValuationsComputeSalesforceAddressValuation',
  'AdminAddressValuationsComputeSalesforceAddressMultiFamilyValuation',
  'ComputeCashFlow',
  // approve / reject
  'AdminAddressValuationsInvestmentsApproveAddressValuation',
  'AdminAddressValuationsRejectAddressValuation',
  // comp fetch
  'AdminAddressValuationsFetchOfferPriceComps',
  'AdminAddressValuationsFetchRentalComps',
  // investment memo — SFR and MF. The MF op was held back on v1 by ACQ-3183
  // while v2's generateAddressInvestmentMemo rejected
  // INVESTMENT_MEMO_TYPE_MULTI_FAMILY; ACQ-3271 ported the MF generator and
  // template to v2, so both memo types now route there.
  'AdminSingleFamilyAddressValuationSummaryGenerateInvestmentMemo',
  'AdminMultiFamilyAddressValuationSummaryGenerateInvestmentMemo',
  // copy page
  'AdminCopyValuationData',
  'AdminSearchSalesforceAccounts',
  'AdminGetAddressesForAccountForCopy',
  // page-load reads
  'AdminAddressValuationsGetSingleFamilyAddressValuation',
  'AdminAddressValuationsGetMultiFamilyAddressValuation',
  'AdminAddressValuationsGetAddressValuationHistory',
  'AdminAddressValuationsGetOfferPriceCompsForAddressValuation',
  'AdminAddressValuationsGetMarketRentCompsForAddressValuation',
  'AdminAddressValuationsGetSalesforceAddress',
  'AdminAddressValuationsGetOperator',
])

export const isValuationCutoverOp = (
  operationName: string | undefined
): boolean => Boolean(operationName && VALUATION_CUTOVER_OPS.has(operationName))

const isBackendName = (value: string | null): value is GqlBackendName =>
  value === 'v1' || value === 'v2'

// localStorage access throws in storage-denied browsers (Safari private
// mode, blocked cookies) — a throw here would crash the Apollo provider, so
// every touchpoint degrades to plain v1 behavior instead
const safeGet = (key: string): string | null => {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

const safeSet = (key: string, value: string): void => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // storage denied — the override just won't stick across navigation
  }
}

const readParam = (): GqlBackendName | null => {
  if (typeof window === 'undefined') return null
  const param = new URLSearchParams(window.location.search).get('gqlBackend')
  return isBackendName(param) ? param : null
}

// Store BOTH values explicitly. Pre-cutover this cleared the key for v1
// (absent == v1), but now that v2 is the default, clearing would resolve
// straight back to v2 — `?gqlBackend=v1` would silently no-op and the
// per-browser rollback lever would be gone exactly when it's needed.
const captureParam = (): void => {
  const param = readParam()
  if (param === null) return
  safeSet(STORAGE_KEY, param)
}

// ACQ-3200: which backend the fleet defaults to, set by env rather than in
// code so the cutover AND its rollback are a Netlify env change + redeploy —
// no PR, no review, no code revert. Anything other than 'v2' means v1, so a
// typo or an unset variable fails safe onto the known-good backend.
//
// NOTE: Gatsby inlines GATSBY_* at BUILD time, so changing this still needs a
// redeploy — it is not a runtime kill switch. The instant lever is the
// per-browser `?gqlBackend=v1` pin below.
export const fleetDefaultBackend = (): GqlBackendName =>
  process.env.GATSBY_VALUATION_GQL_DEFAULT === 'v2' ? 'v2' : 'v1'

// A stored per-browser pin always wins over the fleet default, in both
// directions: it lets one analyst roll back while everyone else stays on v2,
// and it lets an engineer canary v2 before the fleet moves.
export const activeGqlBackend = (): GqlBackendName => {
  const stored = safeGet(STORAGE_KEY)
  return isBackendName(stored) ? stored : fleetDefaultBackend()
}

// v2SplitUri is defined only while v2 is active AND the build carries a v2
// URL — apollo.tsx installs the valuation-op split link iff set. A build
// missing GATSBY_APOLLO_URL_V2 therefore fails safe to v1 rather than
// silently losing valuation traffic.
export const resolveGqlBackend = (): { v2SplitUri: string | undefined } => {
  captureParam()
  if (activeGqlBackend() !== 'v2') return { v2SplitUri: undefined }
  const v2Uri = process.env.GATSBY_APOLLO_URL_V2
  if (!v2Uri) {
    // eslint-disable-next-line no-console
    console.warn(
      '[gqlBackendOverride] v2 is active but GATSBY_APOLLO_URL_V2 is unset in this build; staying on v1'
    )
    return { v2SplitUri: undefined }
  }
  return { v2SplitUri: v2Uri }
}

// Fixed corner badge, shown whenever THIS browser deviates from the fleet
// default (ACQ-3200) — a v2 canary before the cutover, a v1 pin after it.
// Badging the default state instead would follow every analyst around
// permanently; what's worth seeing is the exception.
// State is set post-mount to avoid an SSR hydration mismatch.
export const GqlBackendBadge = () => {
  const [pinned, setPinned] = React.useState<GqlBackendName | null>(null)
  React.useEffect(() => {
    const active = activeGqlBackend()
    setPinned(active === fleetDefaultBackend() ? null : active)
  }, [])
  if (pinned === null) return null
  const revertTo = pinned === 'v2' ? 'v1' : 'v2'
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 8,
        left: 8,
        zIndex: 9999,
        background: '#b45309',
        color: '#fff',
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 12,
        fontFamily: 'monospace',
        opacity: 0.9,
        pointerEvents: 'none',
      }}
    >
      {`valuation GQL → ${pinned} (pinned) · ?gqlBackend=${revertTo} restores`}
    </div>
  )
}
